import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import isNull from 'lodash/isNull';
import { Box, Typography } from '@mui/material';
import { Card } from 'components/@shared/Card';
import { AppointmentResponseViewModel } from 'types/appointment';
import { BackdropLoader } from 'components/@shared/Loaders';
import { useNotifications } from 'hooks/useNotifications';
import { getAppointmentDetails, updateAppointmentConfirmation } from 'services/appointmentService';
import { useDidMount } from 'hooks/useDidMount';
import { AppointmentDetails } from 'components/Appointments/Details';
import { AppointmentComments } from 'components/Appointments/Comments';
import { AppointmentProposals } from 'components/Appointments/Details/AppointmentProposals';
import { AppointmentInvoice } from 'components/Appointments/Details/AppointmentInvoice';
import { AppointmentStatusLabel } from 'components/Appointments/Statuses';
import { viewAppointmentSelector } from 'pages/ViewAppointment/selector';
import { Roles } from 'constants/roles';
import { Headline } from 'components/@shared/Headline';
import { updateProposalConfirmation } from 'services/proposalService';
import { APPOINTMENT_SENT_STATUS, APPOINTMENT_COMPLETE_STATUS } from 'pages/Appointments/constants';

const cardStyles = {
    maxWidth: '1400px',
    margin: 'auto',
};
export const ViewAppointmentPage = () => {
    const { userId, role } = useSelector(viewAppointmentSelector);
    const [isInitLoading, setIsInitLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [initialData, setInitialData] = useState<AppointmentResponseViewModel | null>(null);

    const notifications = useNotifications();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const handleConfirm = useCallback(
        (isConfirmed: boolean) => {
            if (id && userId) {
                setIsLoading(true);
                updateAppointmentConfirmation(id, { isConfirmed, userId })
                    .then((data) => {
                        setInitialData(data);
                        notifications.show({
                            message: `Appointment successfully ${isConfirmed ? 'confirmed' : 'declined'}`,
                        });
                    })
                    .finally(() => setIsLoading(false));
            }
        },
        [id, userId, notifications],
    );

    const handleProposalConfirm = useCallback(
        (id: string, isConfirmed: boolean) => {
            setIsLoading(true);
            updateProposalConfirmation(id, isConfirmed)
                .then((proposal) => {
                    setInitialData((prevState) =>
                        isNull(prevState)
                            ? null
                            : {
                                  ...prevState,
                                  proposals: prevState.proposals.map((p) => (p.id === id ? proposal : p)),
                              },
                    );
                    notifications.show({
                        message: `Proposal successfully ${isConfirmed ? 'confirmed' : 'declined'}`,
                    });
                })
                .finally(() => setIsLoading(false));
        },
        [id, userId, notifications],
    );

    useDidMount(() => {
        if (id) {
            getAppointmentDetails(id)
                .then(setInitialData)
                .finally(() => setIsInitLoading(false));
        }
    });

    if (isInitLoading) {
        return <BackdropLoader />;
    }

    if (isNull(initialData)) {
        return <Typography>No data</Typography>;
    }

    return (
        <Box width="100%" px={2.5} py={2}>
            {isLoading && <BackdropLoader />}
            <Headline title="Appointment details" sx={cardStyles} />
            <Card
                title={
                    <Box mb={1} display="flex" gap={1} alignItems="center" justifyContent="center">
                        <Typography variant="h4" textAlign="center">
                            {initialData.title}
                        </Typography>
                        {initialData.status && <AppointmentStatusLabel status={initialData.status} />}
                    </Box>
                }
                sx={cardStyles}
            >
                <AppointmentDetails data={initialData} onConfirm={role === Roles.Admin ? handleConfirm : undefined} />
            </Card>
            <Card title="Proposals" sx={{ ...cardStyles, mt: 2 }}>
                {id && (
                    <AppointmentProposals
                        appointmentId={id}
                        proposals={initialData.proposals}
                        showActions={initialData.status !== APPOINTMENT_SENT_STATUS}
                        onProposalConfirm={handleProposalConfirm}
                    />
                )}
            </Card>
            <Card title="Invoice" sx={{ ...cardStyles, mt: 2 }}>
                {id && (
                    <AppointmentInvoice
                        appointmentId={id}
                        invoice={initialData.invoice}
                        showActions={
                            !([APPOINTMENT_SENT_STATUS, APPOINTMENT_COMPLETE_STATUS] as string[]).includes(
                                initialData.status,
                            )
                        }
                    />
                )}
            </Card>
            <Card title="Comments" sx={{ ...cardStyles, mt: 2 }}>
                <AppointmentComments />
            </Card>
        </Box>
    );
};
